import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, Injector, input, Input, model, OnDestroy, OnInit, output, signal, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, FormsModule, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD, MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from "@angular/material/tooltip";
import { Subject } from 'rxjs';
import { ProjectRole } from '../../../models/enums';
import { jaroWinklerDistance } from '../../../utils/utils';
import { AddItemConfirmDialogComponent } from '../../dialogs/add-item-confirm-dialog/add-item-confirm-dialog.component';
import { mapToNestedOptions, NestedOption, Option, separatorKeysCodes } from '../nested-options';


export const projectRoleOptions = Object.entries(ProjectRole).map(([key, value]) => ({
  key,
  value
}));

@Component({
  selector: 'app-advanced-chip-select',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatCheckboxModule,
    MatChipsModule,
    MatButtonModule,
    MatDialogModule,
    MatSnackBarModule,
    MatTooltipModule,
],
  templateUrl: './advanced-chip-select.component.html',
  styleUrl: './advanced-chip-select.component.scss',
  encapsulation: ViewEncapsulation.None, // this is necessary to handle local styles properly
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: AdvancedChipSelectComponent
    },
    // {
    //   provide: NG_VALUE_ACCESSOR,
    //   useExisting: forwardRef(() => AdvancedChipSelectComponent),
    //   multi: true,
    // },
    // {
    //   provide: NG_VALIDATORS,
    //   useExisting: forwardRef(() => AdvancedChipSelectComponent),
    //   multi: true,
    // },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedChipSelectComponent implements OnInit, OnDestroy, MatFormFieldControl<Option[]>, ControlValueAccessor {
  static _nextId = 0; // for MatFormFieldControl
  // Model values
  private _allOptions: Option[] = [];

  // Inputs and outputs
  readonly _required = input<boolean, unknown>(false, {
    alias: 'required',
    transform: booleanAttribute,
  });
  readonly _placeholder = input<string>('', { alias: 'placeholder' });
  readonly _allowNewItem = input<boolean>(false);

  @Input({ required: true }) // all available options 
  set allOptions(value: Option[]) {
    this._allOptions = value;
    this.allItems.set(mapToNestedOptions(value, true) ?? []);
  }

  addOption = output<string>();

  readonly _value = model<Option[] | null>([], { alias: 'value' });


  // Signals and computed
  readonly touched = signal(false); // for MatFormFieldControl
  readonly _disabledSignal = signal(false); // component may be dynamically enabled or disabled
  readonly _requiredSignal = signal(false); // component may be dynamically made required or optional
  readonly _focused = signal(false);

  readonly isPanelOpen = signal<boolean>(false); // flag controlling the list panel open
  readonly expandedGroups = signal<Set<string>>(new Set()); // list of manually expanded groups
  readonly allItems = signal<NestedOption[]>([]); // Signal containing all available options
  readonly selectedItems = signal<NestedOption[]>([]); // Signal containing selected options


  readonly filterString = signal(''); // value of input control

  readonly selectedValues = computed(() => {
    return this.selectedItems().map(i => i.value)
  })
  readonly filteredItems = computed(() => {
    const filter = (this.filterString() || '').toLowerCase();
    const items = this.allItems();
    const selectedItems = this.selectedItems();
    // following meeting of 20251126, it is decided that when a filter string is applied,
    // it should apply to top level elements only (top-level items + categories), not to
    // nested items. However, categories should be open
    // console.log('v', v)
    let v = this.filterOptions(items, selectedItems, filter);
    return v;
  });
  // A group should be expanded if:
  // - it is manually expanded by the user
  // - it matches the filter and has children

  // function that checks if a group is expanded or not
  // this is a function that returns a signal, so has to be called with double ()
  isExpanded = (groupId: string) =>
    computed(() => {
      const filterString = this.filterString();
      const isManuallyExpanded = this.expandedGroups().has(groupId);
      const isAutoExpanded = this.filteredItems().some(item => (item.isGroup && item.children && filterString));
      return isManuallyExpanded || isAutoExpanded;
    });


  readonly _snackBar = inject(MatSnackBar);

  readonly stateChanges = new Subject<void>(); // for MatFormFieldControl
  readonly controlType = 'advanced-chip-select'; // for MatFormFieldControl
  readonly id = `app-advanced-chip-select-${AdvancedChipSelectComponent._nextId++}`;

  // getters
  get disabled(): boolean {
    return this._disabledSignal();
  }
  get required(): boolean {
    return this._requiredSignal();
  }

  readonly optionsFormGroup = new FormGroup({
    options: new FormControl<string | null>(null),
  });

  protected readonly _formField = inject(MAT_FORM_FIELD, {
    optional: true,
  });

  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  // START mat-form-field implementation
  get value(): Option[] | null {
    return this._value();
  }
  get placeholder(): string {
    return this._placeholder();
  }
  get focused(): boolean {
    return this._focused();
  }
  get empty() {
    return !this.selectedItems().length;
  }
  get shouldLabelFloat() {
    return true;
  }
  get errorState(): boolean {
    const control = this.ngControl?.control;
    if (!control) {
      return false;
    }
    const matcherError = control.invalid && (control.touched || control.dirty);
    const requiredError = this._required() && this.empty && (control.touched || control.dirty);
    // if (this.ngControl?.name === 'stakeholderGroups') {
    //   console.debug(`errorState: control ${this.ngControl?.name || 'noname'} empty: ${this.empty} invalid: ${control.invalid}, touched: ${control.touched}, dirty: ${control.dirty}, matcherError: ${matcherError}, requiredError: ${requiredError}`);
    //   console.debug(this.ngControl)
    // }
    return matcherError || requiredError;
  }
  describedByIds?: string[] | undefined;
  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement.querySelector(
      '.advanced-chip-select-container',
    )!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick() { }

  onFocusIn() {
    // console.log('onFocusIn')
    if (!this._focused()) {
      this._focused.set(true);
    }
  }

  onFocusOut(event: FocusEvent) {
    // console.log('onFocusOut')
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched.set(true);
      this._focused.set(false);
      this.onTouched();
    }
  }

  // autofilled?: boolean | undefined;
  // userAriaDescribedBy?: string | undefined;
  // disableAutomaticLabeling?: boolean | undefined;

  // END mat-form-field implementation


  @ViewChild('panelTemplate') panelTemplate!: TemplateRef<any>;
  @ViewChild('componentWrapper') componentWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('inputEl') filterInput!: ElementRef<HTMLInputElement>;
  overlayRef!: OverlayRef;

  readonly separatorKeysCodes = separatorKeysCodes;

  public ngControl: NgControl | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(private readonly overlay: Overlay, private readonly vcr: ViewContainerRef,
    private readonly injector: Injector, private readonly dialog: MatDialog, private formBuilder: FormBuilder) {

    const ngControl = this.injector.get(NgControl, null);
    if (ngControl) {
      ngControl.valueAccessor = this;
      this.ngControl = ngControl;

      ngControl.control?.statusChanges
        ?.pipe(takeUntilDestroyed())
        .subscribe(() => {
          console.debug('ngControl statusChanged');
          const isDisabled = ngControl.control?.disabled ?? false;
          this.setDisabledState(isDisabled);
        });

      // initial state
      this.setDisabledState(ngControl.control?.disabled ?? false);
    }

    this.optionsFormGroup = this.formBuilder.group({
      options: this.formBuilder.control('', Validators.required),
    })

    this.optionsFormGroup.statusChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      // this.stateChanges.next();
      console.log('statusChanges') 
    });

    this.optionsFormGroup.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      // console.log('valueChanges')
    });

    this._requiredSignal.set(this._required());

    effect(() => {
      this._requiredSignal.set(this._required());
    });
  }


  ngOnInit() {
    // console.debug('ngOnInit', this.ngControl?.name, this._value(), this._disabledSignal(), this._requiredSignal(), this._required());
  }

  ngOnDestroy() {
    this.stateChanges.complete();
  }


  // START ControlValueAccessor hooks
  // should be called when the user changes the value
  onChange = (_: any) => {
    console.log('CVA onChange');
  };
  onTouched = () => {
    console.log('CVA onTouched')
  };
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }

  // called when the parent writes a value into the component
  writeValue(value: { id: number, name: string; categoryId?: number | null; categoryName?: string | null }[]): void {
    // sets initial values received from parent
    // console.log('writeValue', value);
    if (value) {
      this.selectedItems.set(value.map(o => ({ value: o.id.toString(), label: o.name, isGroup: false })));
    } else {
      this.selectedItems.set([])
    }
    const selectedIds = this.selectedItems().map(o => o.value);
    if (selectedIds) {
      this._value.set(this._allOptions.filter(o => selectedIds.includes(o.id.toString())))
    } else {
      this._value.set([])
    }
    this.filterString.set('');
    // console.debug('onChange', this._value())
    // this.onChange(this._value());
    // Optional: mark pristine so dirty is not true on page load
    // const control = this.ngControl?.control;
    // if (control) {
    //   control.markAsPristine();
    // }
  }

  setDisabledState(isDisabled: boolean): void {
    // console.debug('set disabled state', this.ngControl?.name , isDisabled, this._disabledSignal());
    if (this._disabledSignal() !== isDisabled) {
      // console.debug('set');
      this._disabledSignal.set(isDisabled);
      this.stateChanges.next();
    }
  }
  // END ControlValueAccessor hooks

  // Validator hook
  // validate(control: AbstractControl): ValidationErrors | null {
  //   if (this.required && this.selectedItems().length === 0) {
  //     return { required: true };
  //   }
  //   return null;
  // }
  // End Validator hook


  openPanel() {
    this.isPanelOpen.set(true);
    if (this.overlayRef) {
      this.overlayRef.detach();
    }

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(this.componentWrapper.nativeElement)
      .withPositions([{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top'
      }])
      .withFlexibleDimensions(false)
      .withDefaultOffsetY(0);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      width: this.componentWrapper.nativeElement.offsetWidth,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      // hasBackdrop: true,              // 👈 adds a transparent backdrop to detect outside clicks
    });

    this.overlayRef.outsidePointerEvents().subscribe(event => {
      const target = event.target as HTMLElement;
      if (
        !this.componentWrapper.nativeElement.contains(target) &&
        !this.panelTemplate.elementRef.nativeElement.contains(target)
      ) {
        this.filterString.set('');
        this.filterInput.nativeElement.value = '';
        this.closePanel();
      }
    });
    this.overlayRef.attach(new TemplatePortal(this.panelTemplate, this.vcr));
  }

  closePanel() {
    this.isPanelOpen.set(false);
    this.filterString.set('');
    this.filterInput.nativeElement.value = '';
    this.overlayRef?.detach();
  }

  private filterOptions(allItems: NestedOption[], selectedItems: NestedOption[], query: string): NestedOption[] {
    // Step 1
    // For non-group items: filter out those that do not match the filter or all if no filter
    // For group items: 
    // -> if group item match, do not filter children and expand
    // -> if group item doesn't match, filter out children that do not match
    // Step 2
    // Remove selected items from all items

    return allItems
      .map(o => {
        // node matches if there's no query string or its label matches the query string
        const matchSelf = !query || o.label.toLowerCase().includes(query);
        let filteredChildren: NestedOption[] = []
        if (o.isGroup) {
          if (matchSelf) {
            // the group name matches, so show all non-selected children
            filteredChildren = o.children!.filter(child => !selectedItems.some(i => i.value === child.value));
            return { ...o, children: filteredChildren };
          } else {
            // the group name doesn't match, so only keep non-selected matching children
            filteredChildren = o.children!.filter(child => !selectedItems.some(i => i.value === child.value)
              && (!query || child.label.toLowerCase().includes(query)))
            console.log('filteredChildren', filteredChildren);
            if (filteredChildren.length > 0) {
              // if some children match, return the item
              return { ...o, children: filteredChildren };
            } else {
              return null
            }
          }
        } else
          if (matchSelf && !selectedItems.some(i => i.value === o.value)) {
            return { ...o };
          } else {
            return null
          }
      })
      .filter(Boolean) as NestedOption[];
  }

  // Expand or collapse option group
  toggleGroup(value: NestedOption) {
    console.debug('toggleGroup');
    const values = new Set(this.expandedGroups());
    if (values.has(value.label)) {
      values.delete(value.label);
    } else {
      values.add(value.label);
    }
    this.expandedGroups.set(values);
  }

  onFilterChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.filterString.set(target.value)
  }

  onFilterFocus() {
    this.openPanel();
  }

  // It is assumed that new items can only be added to flat lists
  addItemToList(event: MatChipInputEvent): void {
    console.debug('addNewItem', event.value, this._allowNewItem());
    const newItem = event.value.trim()
    // First check that item is not present yet in the list; if it is, add it to the selection
    let n = this._allOptions.filter((v) => v.name.trim().toLocaleLowerCase() == newItem.toLocaleLowerCase())
      .map(v => ({ isGroup: false, label: v.name, value: String(v.id) }))
    if (n.length == 1) {
      this.addItem(n[0])
      return;
    }

    if (!this._allowNewItem()) {
      this._snackBar.open('❌ Please select an item from the list', 'Close', { duration: 3000 });
      return;
    }

    let similarItems = this._allOptions.filter(v => jaroWinklerDistance(v.name, event.value) > 0.8)
      .map(v => v.name)

    const dialogRef = this.dialog.open(AddItemConfirmDialogComponent, {
      data: {
        itemName: 'initiative',
        newItem: newItem,
        similarItems: similarItems,
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Push new item to the parent component
        this.addOption.emit(event.value);
        this.filterString.set('');
        console.debug('addItemToList selectedItems', this.selectedItems(), this._allOptions)
      }
    });
  }

  addItem(option: NestedOption) {
    console.debug('addItem')
    const newItems = [...this.selectedItems(), option]
    this.selectedItems.set(newItems)
    const selectedIds = this.selectedItems().map(o => o.value);
    if (selectedIds) {
      this._value.set(this._allOptions.filter(o => selectedIds.includes(o.id.toString())))
    } else {
      this._value.set([])
    }
    this.closePanel();
    this.filterString.set('');
    this.onTouched()
    this.onChange(this._value());
  }

  removeItem(option: NestedOption) {
        console.debug('removeItem')
    const newItems = this.selectedItems().filter(i => i.value != option.value)
    this.selectedItems.set(newItems)
    const selectedIds = this.selectedItems().map(o => o.value);
    if (selectedIds) {
      this._value.set(this._allOptions.filter(o => selectedIds.includes(o.id.toString())))
    } else {
      this._value.set([])
    }
    this.onTouched()
    this.onChange(this._value());
  }
}
